import {customAttribute, inject, bindable, bindingMode, InlineViewStrategy,
    //ViewEngine, Container, ViewResources
    TemplatingEngine
}
from "aurelia-framework";

import {AureliaHelperService} from "../../../services/aurelia-helper-service";
import { I18N } from 'aurelia-i18n';

export interface ITemplateSpec {
    content: string;
    bindingContext: any;
    selectionPath: string; // how to find the root element in the template
}

@customAttribute('popover')
@inject(Element, TemplatingEngine, AureliaHelperService, I18N)
export class Popover {
    element: HTMLElement;
    @bindable({ defaultValue: 0, defaultBindingMode: bindingMode.oneWay })
    delay: number | Object;

    @bindable({ defaultValue: "top", defaultBindingMode: bindingMode.oneWay })
    placement: string | Function;
    /**
     * can be translatable text key
     */
    @bindable({ defaultValue: null, defaultBindingMode: bindingMode.oneWay })
    title: string;

    /**
     * can be translatable text key
     */
    @bindable({ defaultValue: null, defaultBindingMode: bindingMode.oneWay, primaryProperty: true  })
    content: string;

    @bindable({ defaultValue: null, defaultBindingMode: bindingMode.oneWay })
    template: ITemplateSpec;

    @bindable({ defaultValue: "body", defaultBindingMode: bindingMode.oneWay })
    container: string | boolean;

    /**
        The usual from bootstrap, plus "hover-modal" that triggers the popover on hover,
        then hides it on click (like with "focus").  This is handy when you want "hover"
        trigger but have controls in the popover that you want the user to be able to
        interact with, but which the default hover does not allow.
    **/
    @bindable({ defaultValue: "hover", defaultBindingMode: bindingMode.oneWay })
    trigger: string;

    @bindable({ defaultValue: true, defaultBindingMode: bindingMode.oneWay })
    enable: boolean;

    @bindable({ defaultValue: true, defaultBindingMode: bindingMode.oneWay })
    animation: boolean;

    @bindable({ defaultValue: false, defaultBindingMode: bindingMode.oneWay })
    html: boolean;

    @bindable({ defaultValue: { selector: 'body', padding: 0 }, defaultBindingMode: bindingMode.oneWay })
    viewport: string | Object | Function;

    //@bindable({ defaultValue: false }) html : boolean;

    private bindingContext: Object;

    constructor(
        element: HTMLElement
        , private templatingEngine: TemplatingEngine
        , private aureliaHelperService: AureliaHelperService
        , private i18n: I18N
    ) {
        this.element = element;
    }

    bind(bindingContext: Object) {
        this.bindingContext = bindingContext;
        
        this.title = this.i18n.tr(this.title);

        this.content = this.i18n.tr(this.content);

        if (this.enable) {
            this.configurePopover();
        }

        if (this.template) {
            // shown is the only one that allows clicks to work inside the template
            $(this.element).on("shown.bs.popover.myTooltip", () => this.enhance());
        }

        if (this.trigger === "hover-modal") {

            let $element = $(this.element);

            let fnHandleMouseOver = () =>
                $element.one('mouseover.myTooltip', () => {
                    $element.focus();
                });

            fnHandleMouseOver();

            $element.on("hide.bs.popover.myTooltip", () => fnHandleMouseOver());
        }
    }

    unbind() {
        if (this.template) {
            $(this.element).off('shown.bs.popover.myTooltip');
        }
    }

    private titleChanged(newValue: string, oldValue: string) {
        this.title = newValue;
    }

    private contentChanged(newValue: string) {
        this.content = newValue;
    }

    private templateChanged(newValue: ITemplateSpec, oldValue: ITemplateSpec) {
        this.template = newValue;
    }

    private enableChanged(newValue: boolean, oldValue: boolean) {

        this.enable = newValue;

        if (newValue) {
            this.configurePopover();
        } else {
            $(this.element).popover("destroy");
        }
    }

    private configurePopover() {

        //console.log("configurePopover");

        // when these are functions, bootstrap will refresh them whenever showing the control
        $(this.element)
            .popover({
                // func so will re-retrieve everytime it shows the popover
                title: () => { return this.title },
                placement: this.placement,
                // if "hover-modal" then we'll send a click on mouseover the element
                trigger: this.trigger === "hover-modal" ? "focus" : this.trigger,
                container: this.container,
                html: this.html,
                animation: this.animation,
                viewport: this.viewport,

                // helps with template enhance for some reason
                delay: this.template ? 100 : 0, 

                // func so will re-retrieve everytime it shows the popover
                content: () => {
                    return this.template ? this.template.content : this.content;
                }
            });
    }

    private enhance() {
        //console.log("enhance");
        this.aureliaHelperService.enhance(
            this.template.selectionPath,
            this.template.bindingContext || this.bindingContext
        );
    }
}